import { useGameLineups, LineupPlayer } from "@/hooks/useGameLineups";
import { usePlayerRatings, PlayerRatingsMap, PlayerRatingsResult } from "@/hooks/usePlayerRatings";
import { useGameEvents } from "@/hooks/useGameEvents";
import { Loader2, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isNationalTeamLeague } from "@/lib/constants";
import { LineupPitchView, PlayerMatchEvents } from "./LineupPitchView";
import { PlayerRatingBadge } from "./PlayerRatingBadge";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import footballGoalIcon from "@/assets/icons/football-goal.png";
interface GameLineupsProps {
  fixtureId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  leagueApiId?: number;
  isLive?: boolean;
  isFinished?: boolean;
  statusShort?: string;
}

const getPositionColor = (pos: string) => {
  switch (pos?.toUpperCase()) {
    case 'G':
      return 'bg-green-500/40 text-green-600 dark:text-green-300 font-semibold';
    case 'D':
      return 'bg-blue-500/40 text-blue-600 dark:text-blue-300 font-semibold';
    case 'M':
      return 'bg-yellow-500/40 text-yellow-600 dark:text-yellow-300 font-semibold';
    case 'F':
      return 'bg-red-500/40 text-red-600 dark:text-red-300 font-semibold';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

// ============= List View Icons (compact inline versions) =============
const FootballListIcon = () => (
  <img 
    src={footballGoalIcon} 
    alt="Goal" 
    className="w-4 h-4 inline-block shrink-0"
  />
);

const YellowCardListIcon = () => (
  <div className="w-2.5 h-3.5 bg-yellow-400 rounded-[1px] border border-yellow-500 inline-block shrink-0" />
);

const RedCardListIcon = () => (
  <div className="w-2.5 h-3.5 bg-red-500 rounded-[1px] border border-red-600 inline-block shrink-0" />
);

const SubstitutionListIcon = ({ minute }: { minute?: number }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      <svg viewBox="0 0 16 16" className="w-4 h-4 inline-block shrink-0 cursor-help">
        <circle cx="8" cy="8" r="7" fill="none" stroke="#9ca3af" strokeWidth="0.5" />
        <path d="M8 1 A7 7 0 0 0 8 15" fill="none" stroke="#ff4040" strokeWidth="3" />
        <path d="M8 1 A7 7 0 0 1 8 15" fill="none" stroke="#00e676" strokeWidth="3" />
      </svg>
    </TooltipTrigger>
    <TooltipContent>
      <p className="text-xs">{minute ? `Subst. ${minute}'` : 'Substituído'}</p>
    </TooltipContent>
  </Tooltip>
);

const PlayerList = ({
  players,
  title,
  showClubLogo = false,
  ratingsMap,
  isLive,
  hasFinishedSnapshots,
  playerEventsMap
}: {
  players: LineupPlayer[];
  title: string;
  showClubLogo?: boolean;
  ratingsMap?: PlayerRatingsMap;
  isLive: boolean;
  hasFinishedSnapshots: boolean;
  playerEventsMap?: Map<number, PlayerMatchEvents>;
}) => (
  <TooltipProvider>
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{title}</h4>
      <div className="space-y-1.5">
        {players.map((player, idx) => {
          const ratingData = player.player_id ? ratingsMap?.get(player.player_id) : undefined;
          const playerEvents = player.player_id ? playerEventsMap?.get(player.player_id) : undefined;
          
          return (
            <div key={idx} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 transition-colors">
              <span className="text-xs font-bold text-muted-foreground w-6">{player.number}</span>
              
              {/* Club logo for national teams, flag for clubs */}
              {showClubLogo && player.team_logo ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <img 
                      src={player.team_logo} 
                      alt={player.team_name || 'Club'} 
                      className="w-5 h-5 object-contain cursor-help transition-transform hover:scale-110"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{player.team_name || 'Unknown club'}</p>
                  </TooltipContent>
                </Tooltip>
              ) : player.nationality_iso ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className={`fi fi-${player.nationality_iso.toLowerCase()} text-lg cursor-help`} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{player.nationality || player.nationality_iso.toUpperCase()}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-help">🏴</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Data not available</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              <span className="flex-1 text-sm">{player.name}</span>
              
              {/* Event icons - goals, cards, substitutions */}
              {playerEvents && (
                <div className="flex items-center gap-1.5">
                  {/* Goals */}
                  {playerEvents.goals > 0 && (
                    <span className="flex items-center gap-0.5">
                      <FootballListIcon />
                      {playerEvents.goals > 1 && (
                        <span className="font-bold text-[10px] text-foreground">{playerEvents.goals}</span>
                      )}
                    </span>
                  )}
                  {/* Cards - red takes priority */}
                  {playerEvents.redCards > 0 ? (
                    <RedCardListIcon />
                  ) : playerEvents.yellowCards > 0 ? (
                    <YellowCardListIcon />
                  ) : null}
                  {/* Substitution */}
                  {playerEvents.wasSubstituted && (
                    <SubstitutionListIcon minute={playerEvents.substituteMinute} />
                  )}
                </div>
              )}
              
              {/* Player Rating Badge */}
              <PlayerRatingBadge 
                ratingData={ratingData} 
                isLive={isLive} 
                hasFinishedSnapshots={hasFinishedSnapshots}
              />
              
              <span className={cn("text-xs px-2 py-0.5 rounded font-medium", getPositionColor(player.pos))}>
                {player.pos}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </TooltipProvider>
);

export function GameLineups({
  fixtureId,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
  leagueApiId,
  isLive = false,
  isFinished = false,
  statusShort
}: GameLineupsProps) {
  const {
    data: lineups,
    isLoading,
    error
  } = useGameLineups(fixtureId);

  // Detect national team games
  const isNationalTeamGame = isNationalTeamLeague(leagueApiId);
  
  // Check if we have grid data for pitch view
  const hasGridData = lineups?.home_startxi?.every(p => p.grid) && lineups?.away_startxi?.every(p => p.grid);

  // Extract all player IDs for ratings lookup
  const allPlayerIds = useMemo(() => {
    if (!lineups) return [];
    const ids: number[] = [];
    
    const addPlayerIds = (players: LineupPlayer[]) => {
      for (const p of players) {
        if (p.player_id && p.player_id > 0) {
          ids.push(p.player_id);
        }
      }
    };
    
    addPlayerIds(lineups.home_startxi || []);
    addPlayerIds(lineups.home_substitutes || []);
    addPlayerIds(lineups.away_startxi || []);
    addPlayerIds(lineups.away_substitutes || []);
    
    return ids;
  }, [lineups]);

  // Debug: track props being passed
  console.log('[GameLineups] Props for ratings:', {
    fixtureId,
    isLive,
    isFinished,
    statusShort,
    playerCount: allPlayerIds.length
  });

  // Fetch player ratings
  const { data: ratingsResult } = usePlayerRatings(
    fixtureId,
    allPlayerIds,
    isLive,
    isFinished
  );
  
  const ratingsMap = ratingsResult?.ratings;
  const hasFinishedSnapshots = ratingsResult?.hasFinishedSnapshots ?? false;

  // Fetch game events for visual indicators
  const { data: events = [] } = useGameEvents(fixtureId, false, statusShort);

  // Aggregate events by player for pitch view indicators
  const playerEventsMap = useMemo(() => {
    const map = new Map<number, PlayerMatchEvents>();
    
    events.forEach(event => {
      if (!event.player_id || event.player_id <= 0) return;
      
      const existing = map.get(event.player_id) || {
        goals: 0, yellowCards: 0, redCards: 0, wasSubstituted: false
      };
      
      if (event.event_type === 'Goal') {
        // Não contar penáltis falhados como golos nos lineups
        if (!event.event_detail?.includes('Missed Penalty')) {
          existing.goals++;
        }
      }
      if (event.event_type === 'Card') {
        if (event.event_detail?.includes('Yellow')) existing.yellowCards++;
        if (event.event_detail?.includes('Red')) existing.redCards++;
      }
      if (event.event_type === 'subst') {
        // player_id = player that goes OUT
        existing.wasSubstituted = true;
        existing.substituteMinute = event.time_elapsed;
      }
      
      map.set(event.player_id, existing);
    });
    
    return map;
  }, [events]);

  // Calculate best player (Man of the Match) - highest rating in finished games
  const bestPlayerId = useMemo(() => {
    if (!ratingsMap || (!isLive && !hasFinishedSnapshots)) return undefined;
    
    let bestId: number | undefined;
    let bestRating = 0;
    
    ratingsMap.forEach((data, playerId) => {
      if (data.currentRating && data.currentRating > bestRating) {
        bestRating = data.currentRating;
        bestId = playerId;
      }
    });
    
    return bestId;
  }, [ratingsMap, hasFinishedSnapshots]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">
            Loading lineups{isNationalTeamGame ? ' and club data' : ''}...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-sm text-muted-foreground">Error loading lineups</p>
      </div>
    );
  }

  if (!lineups) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="space-y-2">
          <p className="text-sm font-medium">Lineups not available</p>
          <p className="text-xs text-muted-foreground max-w-md">
            {isNationalTeamGame 
              ? 'Some historical games may not have lineup records in API-Football. Recent games will have complete lineups 1 hour before kickoff.'
              : 'Team sheets are typically released 20-40 minutes before kickoff.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-6">
      {/* Pitch View (shown first if grid data exists) */}
      {hasGridData && (
        <LineupPitchView
          lineups={lineups}
          homeTeamName={homeTeamName}
          awayTeamName={awayTeamName}
          homeTeamLogo={homeTeamLogo}
          awayTeamLogo={awayTeamLogo}
          ratingsMap={ratingsMap}
          isLive={isLive}
          hasFinishedSnapshots={hasFinishedSnapshots}
          playerEventsMap={playerEventsMap}
          bestPlayerId={bestPlayerId}
        />
      )}

      {/* List View (always shown below) */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Home Team */}
        <div className="space-y-4 p-4 rounded-lg border border-border bg-card/50">
          {/* Team Header */}
          <div className="flex items-center gap-3 pb-3 border-b border-border">
            {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-10 h-10 object-contain" />}
            <div className="flex-1">
              <h3 className="font-bold">{homeTeamName}</h3>
              <p className="text-sm text-muted-foreground">
                Formation: <span className="font-semibold text-foreground">{lineups.home_formation}</span>
              </p>
            </div>
          </div>

          {/* Coach */}
          {lineups.home_coach_name && (
            <div className="flex items-center gap-2 py-2 px-3 rounded bg-muted/50">
              {lineups.home_coach_photo && (
                <img src={lineups.home_coach_photo} alt={lineups.home_coach_name} className="w-8 h-8 rounded-full object-cover" />
              )}
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Manager</p>
                <p className="text-sm font-semibold">{lineups.home_coach_name}</p>
              </div>
            </div>
          )}

          {/* Starting XI */}
          <PlayerList 
            players={lineups.home_startxi} 
            title="Starting XI" 
            showClubLogo={isNationalTeamGame}
            ratingsMap={ratingsMap}
            isLive={isLive}
            hasFinishedSnapshots={hasFinishedSnapshots}
            playerEventsMap={playerEventsMap}
          />

          {/* Substitutes */}
          {lineups.home_substitutes.length > 0 && (
            <PlayerList 
              players={lineups.home_substitutes} 
              title="Substitutes" 
              showClubLogo={isNationalTeamGame}
              ratingsMap={ratingsMap}
              isLive={isLive}
              hasFinishedSnapshots={hasFinishedSnapshots}
              playerEventsMap={playerEventsMap}
            />
          )}
        </div>

        {/* Away Team */}
        <div className="space-y-4 p-4 rounded-lg border border-border bg-card/50">
          {/* Team Header */}
          <div className="flex items-center gap-3 pb-3 border-b border-border">
            {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-10 h-10 object-contain" />}
            <div className="flex-1">
              <h3 className="font-bold">{awayTeamName}</h3>
              <p className="text-sm text-muted-foreground">
                Formation: <span className="font-semibold text-foreground">{lineups.away_formation}</span>
              </p>
            </div>
          </div>

          {/* Coach */}
          {lineups.away_coach_name && (
            <div className="flex items-center gap-2 py-2 px-3 rounded bg-muted/50">
              {lineups.away_coach_photo && (
                <img src={lineups.away_coach_photo} alt={lineups.away_coach_name} className="w-8 h-8 rounded-full object-cover" />
              )}
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Manager</p>
                <p className="text-sm font-semibold">{lineups.away_coach_name}</p>
              </div>
            </div>
          )}

          {/* Starting XI */}
          <PlayerList 
            players={lineups.away_startxi} 
            title="Starting XI" 
            showClubLogo={isNationalTeamGame}
            ratingsMap={ratingsMap}
            isLive={isLive}
            hasFinishedSnapshots={hasFinishedSnapshots}
            playerEventsMap={playerEventsMap}
          />

          {/* Substitutes */}
          {lineups.away_substitutes.length > 0 && (
            <PlayerList 
              players={lineups.away_substitutes} 
              title="Substitutes" 
              showClubLogo={isNationalTeamGame}
              ratingsMap={ratingsMap}
              isLive={isLive}
              hasFinishedSnapshots={hasFinishedSnapshots}
              playerEventsMap={playerEventsMap}
            />
          )}
        </div>
      </div>
    </div>
  );
}
